import React, { useEffect, useState } from 'react';
import { verifyFallbackGate, fallbackLogin } from '../services/api';
import { Icons } from './Icons';

type GateStatus = 'checking' | 'verified' | 'failed';

/**
 * Standalone password-based login page, used only as an emergency fallback
 * when the magic-link email can't be delivered (e.g. email provider outage).
 *
 * Reached at /password/:code - not linked from anywhere in the app. The
 * :code segment is checked server-side against LOGIN_FALLBACK_PATH_CODE as
 * soon as this page loads; only on success does the server hand back a
 * short-lived signed httpOnly cookie, and only then does this page reveal
 * the email + password form. The password submission is checked against
 * FALLBACK_LOGIN_PASSWORD server-side and must carry that same cookie.
 * Neither secret is ever sent to or stored in the browser bundle/JS - both
 * live only in env vars on the server.
 */
export function FallbackLoginPage({ code }: { code: string }) {
  const [gateStatus, setGateStatus] = useState<GateStatus>('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyFallbackGate(code)
      .then(() => setGateStatus('verified'))
      .catch(() => setGateStatus('failed'));
  }, [code]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await fallbackLogin(email, password);
      setSuccess(true);
      // Full page navigation so the normal app boots fresh and picks up
      // the new session cookie via its usual checkAuth() on mount.
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}
      >
        <div
          className="p-6 text-center"
          style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-600/20 mb-4">
            <Icons.Server className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Fallback Sign-In</h1>
          <p className="text-gray-400 text-sm mt-1">Use only when email delivery is unavailable</p>
        </div>

        <div className="p-6">
          {gateStatus === 'checking' && (
            <div className="text-center py-4">
              <Icons.Spinner className="w-6 h-6 mx-auto text-blue-400 mb-3" />
              <p className="text-gray-400 text-sm">Verifying access...</p>
            </div>
          )}

          {gateStatus === 'failed' && (
            <div className="text-center py-4">
              <p className="text-red-400 text-sm">This link is invalid or has expired.</p>
            </div>
          )}

          {gateStatus === 'verified' && (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-600/20 border border-red-600/30">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              {success && (
                <div className="mb-4 p-3 rounded-lg bg-green-600/20 border border-green-600/30">
                  <p className="text-green-400 text-sm">Signed in - redirecting...</p>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="input-modern"
                  autoFocus
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-2">Fallback password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Fallback password"
                  className="input-modern"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={!email || !password || loading}
                className="w-full btn btn-primary py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Icons.Spinner className="w-4 h-4" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
